// Copyright 2026, Auxilio Technologies LLC

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import ControlTower from './pages/ControlTower';
import Shipments from './pages/Shipments';
import RouteOptimization from './pages/RouteOptimization';
import Forecasting from './pages/Forecasting';
import SupplierScorecard from './pages/SupplierScorecard';
import Analytics from './pages/Analytics';
import Alerts from './pages/Alerts';
import Sites from './pages/Sites';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import Feedback from './pages/Feedback';

// Auth guard component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuthStore();

  return (
    <Router>
      <Routes>
        {/* Public route */}
        <Route
          path="/"
          element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />}
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/control-tower" element={<ControlTower />} />
                  <Route path="/shipments" element={<Shipments />} />
                  <Route path="/routes" element={<RouteOptimization />} />
                  <Route path="/forecasting" element={<Forecasting />} />
                  <Route path="/suppliers" element={<SupplierScorecard />} />
                  <Route path="/analytics" element={<Analytics />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/sites" element={<Sites />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/admin/users" element={<UserManagement />} />
                  <Route path="/feedback" element={<Feedback />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

export default App;
